import { makeAutoObservable, observable, runInAction } from "mobx";

import { fetchTenantUsersForPicker } from "@/store/userApi";
import { getCacheAge, isCacheFresh } from "@/store/propertyCore/propertyCacheUtils";
import { IS_DEV } from "@/utils/env";

/** Stale-while-revalidate window; middle of 2–5 min (PropertyList uses 2 min for its list cache). */
const DEFAULT_REVALIDATE_AFTER_MS = 3 * 60 * 1000;

/**
 * Cached tenant user directory for the superadmin acting-user picker (getAllUsers via {@link fetchTenantUsersForPicker}).
 * Cache key: trimmed `customerId` (extend with active filter if the UI gains one).
 *
 * @class
 */
class ActingUserDirectoryStore {
  /** @type {import("mobx").ObservableMap<string, { rows: object[], fetchedAt: number }>} */
  cacheByCustomerId = observable.map();

  /** @type {import("mobx").ObservableMap<string, string>} */
  errorByCustomerId = observable.map();

  /** @type {import("mobx").ObservableMap<string, boolean>} */
  loadingByCustomerId = observable.map();

  /** @type {Map<string, Promise<void>>} */
  _inFlightByKey = new Map();

  defaultRevalidateAfterMs = DEFAULT_REVALIDATE_AFTER_MS;

  _generation = 0;

  constructor() {
    makeAutoObservable(
      this,
      {
        cacheByCustomerId: false,
        errorByCustomerId: false,
        loadingByCustomerId: false,
        _inFlightByKey: false,
      },
      { autoBind: true },
    );
  }

  /** Clear all directory state (logout). */
  reset() {
    this._generation += 1;
    this.cacheByCustomerId.clear();
    this.errorByCustomerId.clear();
    this.loadingByCustomerId.clear();
    this._inFlightByKey.clear();
  }

  /**
   * @param {string} customerId
   * @returns {string}
   */
  _key(customerId) {
    return String(customerId ?? "").trim();
  }

  /**
   * @param {string} customerId
   * @returns {object[]}
   */
  getRows(customerId) {
    const k = this._key(customerId);
    if (!k) return [];
    return this.cacheByCustomerId.get(k)?.rows ?? [];
  }

  /**
   * @param {string} customerId
   * @returns {number|null}
   */
  getLastFetchedAt(customerId) {
    const k = this._key(customerId);
    if (!k) return null;
    const at = this.cacheByCustomerId.get(k)?.fetchedAt;
    return typeof at === "number" ? at : null;
  }

  /**
   * @param {string} customerId
   * @returns {string}
   */
  getError(customerId) {
    const k = this._key(customerId);
    if (!k) return "";
    return this.errorByCustomerId.get(k) ?? "";
  }

  /**
   * @param {string} customerId
   * @returns {boolean}
   */
  isLoading(customerId) {
    const k = this._key(customerId);
    if (!k) return false;
    return Boolean(this.loadingByCustomerId.get(k));
  }

  /**
   * @param {string} customerId
   * @returns {boolean}
   */
  _isFresh(customerId) {
    const k = this._key(customerId);
    const entry = this.cacheByCustomerId.get(k);
    if (!entry) return false;
    const age = getCacheAge(entry.fetchedAt);
    return isCacheFresh(age, null, this.defaultRevalidateAfterMs);
  }

  /**
   * Fire-and-forget warm cache for the active tenant (superadmin sidebar).
   *
   * @param {string} customerId
   */
  prefetchForCustomer(customerId) {
    const k = this._key(customerId);
    if (!k) return;
    void this.ensureLoaded(customerId, { force: false });
  }

  /**
   * Load directory if missing or stale (stale-while-revalidate: keeps showing prior rows while refetching).
   *
   * @param {string} customerId
   * @param {{ force?: boolean }} [options] - `force`: bypass TTL and cache (used by refresh).
   * @returns {Promise<void>}
   */
  async ensureLoaded(customerId, options = {}) {
    const force = Boolean(options.force);
    const k = this._key(customerId);
    if (!k) return;

    if (!force && this._isFresh(k)) {
      return;
    }

    const inFlightPromise = this.inFlightPromiseFor(k);
    if (inFlightPromise) return inFlightPromise;

    const cached = this.cacheByCustomerId.get(k);
    const hasRows = Array.isArray(cached?.rows) && cached.rows.length > 0;
    const showLoading = force || !hasRows;

    const gen = this._generation;
    const p = this._fetchAndStore(k, showLoading, gen);
    this._inFlightByKey.set(k, p);
    try {
      await p;
    } finally {
      if (this._inFlightByKey.get(k) === p) {
        this._inFlightByKey.delete(k);
      }
    }
  }

  /**
   * @param {string} k - normalized cache key
   * @returns {Promise<void>|undefined}
   */
  inFlightPromiseFor(k) {
    return this._inFlightByKey.get(k);
  }

  /**
   * Invalidate TTL for this tenant and refetch; list shows loading while in flight.
   *
   * @param {string} customerId
   * @returns {Promise<void>}
   */
  async refresh(customerId) {
    const k = this._key(customerId);
    if (!k) return;

    const pending = this._inFlightByKey.get(k);
    if (pending) {
      try {
        await pending;
      } catch {
        /* superseded by refresh */
      }
    }

    runInAction(() => {
      this.cacheByCustomerId.delete(k);
      this.errorByCustomerId.delete(k);
    });

    await this.ensureLoaded(customerId, { force: true });
  }

  /**
   * @param {string} k
   * @param {boolean} showLoading
   * @param {number} generationAtStart
   * @returns {Promise<void>}
   */
  async _fetchAndStore(k, showLoading, generationAtStart) {
    runInAction(() => {
      if (showLoading) this.loadingByCustomerId.set(k, true);
      this.errorByCustomerId.delete(k);
    });

    try {
      const rows = await fetchTenantUsersForPicker(k);
      if (this._generation !== generationAtStart) return;

      runInAction(() => {
        this.cacheByCustomerId.set(k, { rows, fetchedAt: Date.now() });
        this.errorByCustomerId.delete(k);
      });
    } catch (err) {
      if (this._generation !== generationAtStart) return;

      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorByCustomerId.set(k, "Could not load users for this account.");
        if (IS_DEV && message) {
          console.log("[ActingUserDirectoryStore] fetch failed", { customerId: k, message });
        }
      });
    } finally {
      if (this._generation === generationAtStart) {
        runInAction(() => {
          this.loadingByCustomerId.set(k, false);
        });
      }
    }
  }
}

export default ActingUserDirectoryStore;
